import { InteractionType, Octopus } from '@maximizer/core/shared/domain';
import { isValid, parseISO } from 'date-fns';
import {
  OutlookTimelineConfiguration,
  OutlookTimelineEvent,
  OutlookTimelineDirection,
} from '../../../models';

export class TimelineConfigurationMapper extends Octopus.ConfigurationMapper<
  Octopus.TimelineFieldOptionsResponse,
  OutlookTimelineConfiguration
> {
  from(
    source: Octopus.TimelineFieldOptionsResponse,
  ): OutlookTimelineConfiguration {
    const timeline = source.Timeline?.FieldOptions;

    return {
      types: timeline?.Type?.map(this.mapInteractionType) ?? [],
      notes: timeline?.NoteType?.map(this.mapValueAndDisplay) ?? [],
      documents: timeline?.DocumentType?.map(this.mapValueAndDisplay) ?? [],
    };
  }
}

export class TimelineMapper extends Octopus.ConfigurationMapper<
  Octopus.Timeline[],
  OutlookTimelineEvent[]
> {
  from(source: Octopus.Timeline[]): OutlookTimelineEvent[] {
    return this.getEvents(source);
  }

  private mapDirection(direction: number): OutlookTimelineDirection {
    if (direction === 1) {
      return OutlookTimelineDirection.Incoming;
    } else if (direction === 2) {
      return OutlookTimelineDirection.Outgoing;
    }
    return OutlookTimelineDirection.None;
  }

  getEvents(source: Octopus.Timeline[]): OutlookTimelineEvent[] {
    this.removeDuplicatedEmails(source);

    return source
      .map((interaction) => this.mapEvent(interaction))
      .filter((event): event is OutlookTimelineEvent => event !== null);
  }

  removeDuplicatedEmails(source: Octopus.Timeline[]): void {
    type Emails = { [key: string]: Octopus.Timeline[] };
    const interactions = source.filter(
      (item) => item.Type?.Key === Octopus.DefaultInteractions.EMAIL,
    );

    if (interactions.length) {
      const emails: Emails = interactions.reduce(function (group, interaction) {
        const key = `${interaction.StartDate?.Value}-${interaction.Creator?.Value}-${interaction.Subject}`;
        group[key] = group[key] || [];
        group[key].push(interaction);
        return group;
      }, {} as Emails);

      Object.keys(emails).forEach((key) => {
        const group = emails[key];
        if (group.length > 1 && group.some((item) => item.With)) {
          group.forEach((interaction) => {
            if (!interaction.With) {
              const index = source.findIndex(
                (item) => item.Key === interaction.Key,
              );
              if (index !== -1) {
                source.splice(index, 1);
              }
            }
          });
        }
      });
    }
  }

  private mapEvent(source: Octopus.Timeline): OutlookTimelineEvent | null {
    const startDate = this.parseStartDate(source.StartDate?.Value);
    if (!startDate) return null;

    const typeKey = source.Type?.Key ?? '';
    const event: OutlookTimelineEvent = {
      key: source.Key ?? '',
      type: this.mapEventType(typeKey),
      interactionType: this.mapEventInteractionType(source.Type),
      subject: source.Subject,
      description: source.Description ? source.Description.trim() : undefined,
      date: {
        value: startDate,
        display: source.StartDate?.DisplayValue ?? '',
      },
      important: source.Important,
      creator: {
        value: source.Creator?.Value ?? '',
        display: source.Creator?.DisplayValue ?? '',
      },
      entriesDisplay: this.mapEntriesDisplay(source),
      direction: this.mapDirection(source.Direction),
    };

    if (Octopus.DefaultInteractions.isNote(typeKey)) {
      event.noteType = source.NoteType;
    }

    if (Octopus.DefaultInteractions.isEmail(typeKey)) {
      event.description = '';
    }

    return event;
  }

  private parseStartDate(value?: string): Date | null {
    if (!value) {
      return null;
    }

    const date = parseISO(value);
    return isValid(date) ? date : null;
  }

  private mapEventInteractionType(
    source?: Octopus.InteractionType,
  ): InteractionType {
    return {
      type: source?.Key ?? '',
      display: source?.DisplayValue ?? '',
      icon: source?.Icon ?? 0,
      external: source?.External ?? false,
    };
  }

  private mapEventType(typeKey: string): OutlookTimelineEvent['type'] {
    if (Octopus.DefaultInteractions.isNote(typeKey)) {
      return 'note';
    }
    if (Octopus.DefaultInteractions.isAppointment(typeKey)) {
      return 'appointment';
    }
    if (Octopus.DefaultInteractions.isTask(typeKey)) {
      return 'task';
    }
    if (Octopus.DefaultInteractions.isEmail(typeKey)) {
      return 'email';
    }
    if (Octopus.DefaultInteractions.isDocument(typeKey)) {
      return 'document';
    }
    return 'interaction';
  }

  private mapEntriesDisplay(source: Octopus.Timeline): string {
    const entries = source.AbEntries || source.Leads;
    if (!entries?.length) {
      return '';
    }

    return entries.map((entry) => entry.DisplayValue).join(', ');
  }
}
